/**
 * storefrontBrandAccent -- item 5 of the 2026-08-14 layout gap fix
 * (docs/planning/2026-08-14-storefront-layout-gap-vs-company-store-comp.md):
 * carry `stores.brand_color` beyond the topbar into headings/accents on the
 * Home/Category/Product pages.
 *
 * A store's brand_color is DISTRIBUTOR-CHOSEN and unconstrained -- it could
 * be deep navy (the common case) or something like neon yellow. Naively
 * using it as both the accent AND the text ink would fail WCAG AA for any
 * light/saturated choice, silently regressing the tf10 type-contrast
 * effort. This module separates the two concerns:
 *
 *   --sf-brand-color: the raw brand color, used for non-text accents
 *     (a border/underline/bar) where AA text-contrast rules don't apply
 *     (WCAG 1.4.11 non-text contrast, 3:1, is the closer bar and a
 *     4px accent bar against a light card easily clears it for any hue).
 *   --sf-brand-ink: a heading-safe color derived FROM the brand color that
 *     always clears AA (4.5:1) against the light card surface storefront
 *     content sits on. For light/vivid brand colors this is NOT the brand
 *     color itself (rendering a near-white heading on a near-white card
 *     would be illegible) -- it darkens toward the brand hue's own dark
 *     end instead of falling back to a generic gray, so the accent still
 *     reads as "this store's color" even when clamped for contrast.
 */
import { hexToRgb, contrastRatio } from "@/utils/luminance";

// The light card surface --sf-brand-ink is guaranteed to clear AA against
// (--tf-surface-light in _tokens.css). Kept as a literal (not imported) so
// this module has no CSS dependency -- if the token value ever changes,
// AA_MIN_RATIO's test coverage (storefrontBrandAccent.test.js) will catch
// drift the next time the token and this constant disagree.
const CARD_SURFACE_HEX = "#f5f8fa";
const AA_MIN_RATIO = 4.5;

// Safe, always-AA-compliant fallback ink (matches --tf-fg-1).
const DEFAULT_INK = "#1c1c1c";

function toHex2(n) {
  return Math.max(0, Math.min(255, Math.round(n)))
    .toString(16)
    .padStart(2, "0");
}

// Scale an {r,g,b} triple toward black by `factor` (0 = unchanged, 1 = black).
function darken(rgb, factor) {
  return {
    r: rgb.r * (1 - factor),
    g: rgb.g * (1 - factor),
    b: rgb.b * (1 - factor),
  };
}

function rgbToHex(rgb) {
  return `#${toHex2(rgb.r)}${toHex2(rgb.g)}${toHex2(rgb.b)}`;
}

/**
 * Derive an AA-safe ink color from a brand hex. If the brand color itself
 * already clears AA against the card surface, use it as-is (the common
 * case for a typical dark/mid brand color -- the store's own color shows
 * up verbatim in headings). Otherwise progressively darken it in fixed
 * steps until it clears AA, so the result still reads as "this brand's
 * hue, just deep enough to be legible" rather than a disconnected gray.
 * Falls through to DEFAULT_INK only if darkening toward black still
 * somehow fails to clear AA (should not happen -- pure black clears AA
 * against any surface lighter than near-black).
 */
function deriveInk(hex) {
  if (contrastRatio(hex, CARD_SURFACE_HEX) >= AA_MIN_RATIO) {
    return hex;
  }
  const rgb = hexToRgb(hex);
  const steps = [0.25, 0.45, 0.6, 0.75, 0.9, 1];
  for (const factor of steps) {
    const candidate = rgbToHex(darken(rgb, factor));
    if (contrastRatio(candidate, CARD_SURFACE_HEX) >= AA_MIN_RATIO) {
      return candidate;
    }
  }
  return DEFAULT_INK;
}

/**
 * Build the CSS custom-property map for a given store brand color.
 *
 * @param {string|null|undefined} brandColor  stores.brand_color (hex).
 * @returns {Record<string,string>} `{ '--sf-brand-color': ..., '--sf-brand-ink': ... }`,
 *   or `{}` for a missing/invalid color -- callers bind this directly as a
 *   Vue `:style` object, and an empty object leaves the CSS `var(...,
 *   fallback)` defaults in the stylesheet in full control (no accent
 *   rendered as broken/transparent).
 */
export function storefrontBrandAccentVars(brandColor) {
  if (!brandColor || typeof brandColor !== "string") return {};
  const rgb = hexToRgb(brandColor);
  if (!rgb) return {};

  return {
    "--sf-brand-color": brandColor,
    "--sf-brand-ink": deriveInk(brandColor),
  };
}

export default storefrontBrandAccentVars;
